import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";
import { Contract } from "@/types/contract";
import { PropertyInfo } from "./PropertyInfo";
import { BuyerInfo } from "./BuyerInfo";
import { OriginalTerms } from "./OriginalTerms";
import { CurrentStatus } from "./CurrentStatus";
import { DueDiligence } from "./DueDiligence";
import { PropertyLinks } from "./PropertyLinks";
import { CorporateNotes } from "./CorporateNotes";

interface CorporateInvestorSummaryProps {
  contract: Contract;
  onClose: () => void;
}

export function CorporateInvestorSummary({ contract, onClose }: CorporateInvestorSummaryProps) {
  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header with close and print buttons */}
      <div className="flex justify-between items-center print:hidden">
        <h2 className="text-2xl font-bold">Corporate Investor Summary</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-2xl font-bold">Corporate Investor Summary</h1>
        <p className="text-muted-foreground">Investment Opportunity Analysis</p>
      </div>

      {/* Property Overview */}
      <PropertyInfo contract={contract} formatCurrency={formatCurrency} />

      {/* Buyer Information */}
      <BuyerInfo contract={contract} />

      {/* Original Contract Terms */}
      <OriginalTerms contract={contract} formatCurrency={formatCurrency} formatDate={formatDate} />

      {/* Current Contract Status */}
      <CurrentStatus contract={contract} formatCurrency={formatCurrency} formatDate={formatDate} />

      {/* Due Diligence Information */}
      <DueDiligence contract={contract} />

      {/* Property Links */}
      <PropertyLinks contract={contract} />

      {/* Corporate Investor Notes */}
      <CorporateNotes contract={contract} />

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground print:mt-8">
        <p>Generated on {new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}</p>
      </div>
    </div>
  );
}